import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './Toast.css';

const ICONS = { success: '✓', warn: '!', err: '×', info: 'i' };

export function Toast({
  kind = 'info',
  message,
  title,
  glyph,
  actionLabel,
  action,
  kbd,
  secondaryActionLabel,
  secondaryAction,
  dismissLabel,
  progress,
  progressLabel,
  progressMeta,
  progressDone = false,
  duration = 0,
  onDismiss,
}) {
  const isLoading = kind === 'loading';
  const hasProgress = typeof progress === 'number';
  const block = Boolean(title) || hasProgress;
  const showTimer = duration > 0 && !isLoading && !hasProgress;
  const hasAction = Boolean(
    (actionLabel && action) || (secondaryActionLabel && secondaryAction),
  );
  // Clicking anywhere on the toast dismisses it. Sticky loading/progress toasts
  // are exempt — a stray click shouldn't hide an operation still in flight.
  const dismissOnBodyClick = Boolean(onDismiss) && !isLoading && !hasProgress;

  return (
    <div
      className={`toast toast--${kind}${block ? ' toast--block' : ''}`}
      role={kind === 'err' ? 'alert' : 'status'}
      aria-live={kind === 'err' ? 'assertive' : 'polite'}
      onClick={dismissOnBodyClick ? onDismiss : undefined}
      style={dismissOnBodyClick ? { cursor: 'pointer' } : undefined}
    >
      <div className="toast__ico">
        {isLoading ? (
          <span className="toast__spin" aria-hidden="true" />
        ) : (
          (glyph ?? ICONS[kind] ?? 'i')
        )}
      </div>

      <span className="toast__grow">
        {title && <span className="toast__title">{title}</span>}
        {title
          ? message && <span className="toast__desc">{message}</span>
          : message}
        {hasProgress && (
          <>
            <div className={`toast__bar${progressDone ? ' done' : ''}`}>
              <i
                style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
              />
            </div>
            {(progressLabel || progressMeta) && (
              <div className="toast__metarow">
                <span className="count">{progressLabel}</span>
                <span>{progressMeta}</span>
              </div>
            )}
          </>
        )}
      </span>

      {hasAction && <span className="toast__sep" />}
      {actionLabel && action && (
        <button className="toast__action" onClick={action}>
          {actionLabel}
          {kbd && <span className="toast__kbd">{kbd}</span>}
        </button>
      )}
      {secondaryActionLabel && secondaryAction && (
        <button
          className="toast__action toast__action--ghost"
          onClick={secondaryAction}
        >
          {secondaryActionLabel}
        </button>
      )}
      {onDismiss && (
        <button
          className="toast__dismiss"
          onClick={onDismiss}
          aria-label="Cerrar"
        >
          {/* dismissLabel only makes sense while loading (e.g. 'en curso') — a
              toast.update() to success/err doesn't clear it, so once resolved
              it must fall back to 'esc' instead of leaking the stale label. */}
          {isLoading ? (dismissLabel ?? 'cancelar') : 'esc'}
        </button>
      )}

      {showTimer && (
        <span
          className="toast__timer"
          style={{ animationDuration: `${duration}ms` }}
        />
      )}
    </div>
  );
}

Toast.propTypes = {
  kind: PropTypes.oneOf(['success', 'warn', 'err', 'info', 'loading']),
  message: PropTypes.node,
  title: PropTypes.node,
  glyph: PropTypes.node,
  actionLabel: PropTypes.string,
  action: PropTypes.func,
  kbd: PropTypes.string,
  secondaryActionLabel: PropTypes.string,
  secondaryAction: PropTypes.func,
  dismissLabel: PropTypes.string,
  progress: PropTypes.number,
  progressLabel: PropTypes.string,
  progressMeta: PropTypes.string,
  progressDone: PropTypes.bool,
  duration: PropTypes.number,
  onDismiss: PropTypes.func,
};

const ToastContext = createContext(null);

let seq = 0;
function nextId() {
  seq += 1;
  return `t_${Date.now()}_${seq}`;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Live view of the stack for the keydown handler, so the listener can stay
  // registered once (see below) instead of re-binding on every toast change.
  const toastsRef = useRef(toasts);
  useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  // Escape dismisses the newest dismissable toast before any layer underneath
  // reacts. Registered ONCE at mount in the bubble phase, which fixes the
  // precedence purely by event order:
  //   • A Modal's Escape listens in the CAPTURE phase + stopPropagation, so a
  //     modal still wins over a toast (Modal > toast).
  //   • RightSidebar's drawer listens in the bubble phase ON THE SAME TARGET
  //     (document) and only mounts when opened — always AFTER this listener.
  //     stopImmediatePropagation (NOT plain stopPropagation) is required: the
  //     drawer's listener is a sibling on the same target, and stopPropagation
  //     only blocks ancestors, not same-target siblings. We fire first (mounted
  //     first) and stopImmediate, so the drawer never sees the Escape while a
  //     toast is up (toast > drawer).
  //   • With no dismissable toast we don't stop anything, so Escape falls
  //     through and closes the drawer exactly as before.
  // Sticky loading/progress toasts are skipped — same rationale as click-to-
  // dismiss: Escape shouldn't hide an operation still in flight.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const list = toastsRef.current;
      for (let i = list.length - 1; i >= 0; i -= 1) {
        const t = list[i];
        const sticky = t.kind === 'loading' || typeof t.progress === 'number';
        if (!sticky) {
          e.stopImmediatePropagation();
          dismiss(t.id);
          return;
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dismiss]);

  const addToast = useCallback(
    (opts) => {
      const { duration, ...rest } = opts || {};
      const id = nextId();
      // Loading and progress toasts stay until resolved; the rest auto-dismiss.
      const sticky =
        rest.kind === 'loading' || typeof rest.progress === 'number';
      const ttl = duration ?? (sticky ? 0 : 4000);
      setToasts((prev) => [...prev, { id, ...rest, duration: ttl }]);
      if (ttl > 0) setTimeout(() => dismiss(id), ttl);
      return id;
    },
    [dismiss],
  );

  // Patch an existing toast in place — used to resolve a loading toast into a
  // success/error, or to advance a progress bar.
  const update = useCallback(
    (id, patch) => {
      setToasts((prev) =>
        prev.map((t) => (t.id === id ? { ...t, ...patch } : t)),
      );
      if (typeof patch?.duration === 'number' && patch.duration > 0) {
        setTimeout(() => dismiss(id), patch.duration);
      }
    },
    [dismiss],
  );

  // Callable for backwards compatibility (`toast({...})`), with helpers attached.
  const api = useMemo(() => {
    const fn = (opts) => addToast(opts);
    fn.show = addToast;
    fn.dismiss = dismiss;
    fn.update = update;
    return fn;
  }, [addToast, dismiss, update]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* `data-rsb-keep-open` exempts toasts from RightSidebar's
          click-outside-to-close: interacting with a toast (action or dismiss)
          must not also close an open drawer behind it. */}
      <div className="toast-provider" data-rsb-keep-open>
        {toasts.map((t) => (
          <Toast key={t.id} {...t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

ToastProvider.propTypes = {
  children: PropTypes.node,
};

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
